// The Enlarged_Viewer (enlarge-preview-annotate task 8.1; Req 1.7, 2.x, 3.1, 3.5, 3.6, 4.5, 6.x, 7.x, 9.1-9.4, 10.1,
// 10.8; design "EnlargedViewer"). A Cloudscape Modal sized to the window that shows one page of an already-loaded
// document with page navigation, zoom, and the Annotation_Layer. It renders only from `loaded`, so opening, paging,
// and zooming send no request for the document. The Modal supplies Escape, the press-and-release overlay rule, the
// focus trap, and the "Close" control in its header, which never scrolls away.
import { useCallback, useEffect, useLayoutEffect, useMemo, useReducer, useRef, useState } from 'react';
import Box from '@cloudscape-design/components/box';
import Button from '@cloudscape-design/components/button';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import Modal from '@cloudscape-design/components/modal';
import Spinner from '@cloudscape-design/components/spinner';
import Toggle from '@cloudscape-design/components/toggle';

import { AnnotationLayer } from '../features/annotations/AnnotationLayer';
import type { PageSize } from '../features/annotations/layout';
import {
  annotationsForPage,
  availability,
  labelsWithMember,
  unlocatedItems,
  unlocatedSummary,
  type AnnotationModel,
} from '../features/annotations/model';
import { useShowAnnotations, useShowValues } from '../features/annotations/settings';
import { useConfidenceThreshold } from '../features/runs/confidenceThreshold';
import { DetailCanvas } from './DetailCanvas';
import type { LoadedDocument } from './loadedDocument';
import { PageImage } from './PageImage';
import { ZOOM_FIT, ZOOM_MAX, ZOOM_MIN, inPageRange, viewerReducer } from './viewerState';

export const DOCUMENT_UNAVAILABLE = 'The document could not be displayed.';
export const PAGE_UNAVAILABLE = 'This page could not be displayed.';
export const LOCATIONS_UNAVAILABLE = 'Field locations are not available for this result.';

export interface EnlargedViewerProps {
  /** Document file name, shown in the heading. */
  name: string;
  loaded: LoadedDocument;
  /** Pages the viewer can show (1 for a TIFF opened from the Document_Viewer). */
  pageCount: number;
  /** 1-based page shown when the viewer opens. */
  initialPage: number;
  /** Absent when opened from the Page_Thumbnails: no annotation controls or layer (Req 4.5). */
  annotations?: AnnotationModel;
  /** Called on Close, Escape, or an overlay click, with the page shown last (Req 3.2). */
  onClose: (lastPage: number) => void;
}

function windowSize() {
  return typeof window === 'undefined'
    ? { width: 1280, height: 800 }
    : { width: window.innerWidth, height: window.innerHeight };
}

/** True when the loaded PDF's proxy was destroyed after it was handed to the viewer (Req 10.8). */
function isGone(loaded: LoadedDocument): boolean {
  if (loaded.kind === 'error') return true;
  if (loaded.kind !== 'pdf') return false;
  const task = (loaded.pdf as { loadingTask?: { destroyed?: boolean } }).loadingTask;
  return task?.destroyed === true;
}

export function EnlargedViewer({ name, loaded, pageCount, initialPage, annotations, onClose }: EnlargedViewerProps) {
  const total = Math.max(1, pageCount);
  const [state, dispatch] = useReducer(viewerReducer, undefined, () => ({
    page: inPageRange(initialPage, total) ? initialPage : 1,
    pageCount: total,
    zoom: ZOOM_FIT,
  }));
  const { page, zoom } = state;

  // Modal size follows the window (Req 2.1); Cloudscape clamps it to the available viewport.
  const [win, setWin] = useState(windowSize);
  useEffect(() => {
    const onResize = () => setWin(windowSize());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Fitted width = the content area's client width, refit on resize at the same zoom percentage (Req 2.1, 2.14).
  // The Modal's Portal mounts its children one commit after this component, so the content element is tracked in
  // state through a callback ref; effects keyed on it run once it exists.
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [scrollEl, setScrollEl] = useState<HTMLDivElement | null>(null);
  const setScrollRef = useCallback((el: HTMLDivElement | null) => {
    scrollRef.current = el;
    setScrollEl(el);
  }, []);
  const [fitWidth, setFitWidth] = useState(0);
  useLayoutEffect(() => {
    const el = scrollEl;
    if (!el) return;
    const update = () => setFitWidth(Math.floor(el.clientWidth));
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scrollEl]);
  const width = fitWidth > 0 ? (fitWidth * zoom) / 100 : 0;

  // A new page starts at the top-left corner (Req 2.10).
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = 0;
    el.scrollLeft = 0;
  }, [page, scrollEl]);

  // The render last shown, and the last page error, each tied to a page and width.
  const [rendered, setRendered] = useState<{ page: number; width: number; size: PageSize } | null>(null);
  const [failed, setFailed] = useState<{ page: number; width: number } | null>(null);
  const onRendered = useCallback(
    (size: PageSize, p: number) => {
      setRendered({ page: p, width, size });
      setFailed(null);
    },
    [width],
  );
  const onError = useCallback(() => setFailed({ page, width }), [page, width]);

  const [showAnnotations, setShowAnnotations] = useShowAnnotations();
  const [showValues, setShowValues] = useShowValues();
  const [threshold] = useConfidenceThreshold();

  const gone = isGone(loaded);
  const shown = gone || loaded.kind === 'loading' || loaded.kind === 'error' ? null : loaded;
  const pageFailed = failed !== null && failed.page === page && failed.width === width;
  const current = rendered !== null && rendered.page === page && rendered.width === width ? rendered : null;
  const rendering = shown !== null && width > 0 && !pageFailed && current === null;

  const avail = annotations ? availability(annotations, total) : 'none';
  const pageAnnotations = useMemo(
    () => (annotations ? annotationsForPage(annotations, page, total) : []),
    [annotations, page, total],
  );
  const withMember = useMemo(
    () => (annotations ? labelsWithMember(annotations, pageAnnotations) : false),
    [annotations, pageAnnotations],
  );
  const unlocated = useMemo(
    () => (annotations ? unlocatedSummary(unlocatedItems(annotations, total)) : null),
    [annotations, total],
  );
  const drawLayer = annotations !== undefined && avail === 'available' && showAnnotations && current !== null;
  const showUnlocated =
    avail === 'available' && showAnnotations && unlocated !== null && unlocated.more + unlocated.paths.length > 0;

  return (
    <Modal
      visible
      width={win.width}
      height={win.height}
      disableContentPaddings
      closeAriaLabel="Close"
      header={`${name} - Page ${page} of ${total}`}
      onDismiss={() => onClose(page)}
    >
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        <div
          role="toolbar"
          aria-label="Page and zoom"
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 8,
            padding: '4px 20px',
            flex: '0 0 auto',
          }}
        >
          {total > 1 && (
            <>
              <Button
                variant="icon"
                iconName="angle-left"
                ariaLabel="Previous page"
                disabled={page <= 1}
                onClick={() => dispatch({ type: 'previous' })}
              />
              <Button
                variant="icon"
                iconName="angle-right"
                ariaLabel="Next page"
                disabled={page >= total}
                onClick={() => dispatch({ type: 'next' })}
              />
            </>
          )}
          <Button
            variant="icon"
            iconName="zoom-out"
            ariaLabel="Zoom out"
            disabled={zoom <= ZOOM_MIN}
            onClick={() => dispatch({ type: 'zoomOut' })}
          />
          <span aria-live="polite" data-testid="zoom-level" style={{ minWidth: 48, textAlign: 'center' }}>
            {`${Math.round(zoom)}%`}
          </span>
          <Button
            variant="icon"
            iconName="zoom-in"
            ariaLabel="Zoom in"
            disabled={zoom >= ZOOM_MAX}
            onClick={() => dispatch({ type: 'zoomIn' })}
          />
          <Button
            variant="icon"
            iconName="zoom-to-fit"
            ariaLabel="Fit to width"
            onClick={() => dispatch({ type: 'fit' })}
          />
          {annotations && avail === 'available' && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginLeft: 'auto' }}>
              <Toggle checked={showAnnotations} onChange={({ detail }) => setShowAnnotations(detail.checked)}>
                Show field annotations
              </Toggle>
              {showAnnotations && (
                <Toggle checked={showValues} onChange={({ detail }) => setShowValues(detail.checked)}>
                  Show values in labels
                </Toggle>
              )}
            </div>
          )}
        </div>

        <div style={{ position: 'relative', flex: '1 1 auto', minHeight: 0 }}>
          <div
            ref={setScrollRef}
            data-testid="enlarged-content"
            style={{ position: 'absolute', inset: 0, overflow: 'auto' }}
          >
            {gone ? (
              <Box padding="l" textAlign="center" color="text-status-error">
                {DOCUMENT_UNAVAILABLE}
              </Box>
            ) : pageFailed ? (
              <Box padding="l" textAlign="center" color="text-status-error">
                {PAGE_UNAVAILABLE}
              </Box>
            ) : (
              shown !== null &&
              width > 0 && (
                <div
                  style={{
                    position: 'relative',
                    width,
                    height: current ? current.size.height : undefined,
                    margin: '0 auto',
                  }}
                >
                  <PageImage
                    loaded={shown}
                    page={page}
                    width={width}
                    name={`${name}, page ${page}`}
                    onRendered={onRendered}
                    onError={onError}
                  />
                  {shown.kind === 'pdf' && current && (
                    <DetailCanvas
                      pdf={shown.pdf}
                      page={page}
                      width={current.size.width}
                      height={current.size.height}
                      scrollContainer={scrollRef}
                    />
                  )}
                  {drawLayer && current && (
                    <AnnotationLayer
                      annotations={pageAnnotations}
                      size={current.size}
                      showValues={showValues}
                      withMember={withMember}
                      threshold={threshold}
                      focusable
                      fontSize={12}
                      scale={zoom / 100}
                    />
                  )}
                </div>
              )
            )}
          </div>
          {(loaded.kind === 'loading' || rendering) && !gone && (
            <div
              data-testid="enlarged-loading"
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Spinner size="large" />
            </div>
          )}
        </div>

        {annotations && avail === 'unavailable' && (
          <div style={{ flex: '0 0 auto', padding: '4px 20px' }}>
            <Box color="text-body-secondary">{LOCATIONS_UNAVAILABLE}</Box>
          </div>
        )}
        {showUnlocated && unlocated && (
          <div style={{ flex: '0 0 auto', padding: '0 20px' }}>
            <ExpandableSection headerText={unlocated.text}>
              <div style={{ maxHeight: '30vh', overflow: 'auto' }}>
                <ul style={{ margin: 0 }}>
                  {unlocated.paths.map((p, i) => (
                    <li key={`${i}:${p}`}>{p}</li>
                  ))}
                </ul>
                {unlocated.more > 0 && <Box>{`and ${unlocated.more} more`}</Box>}
              </div>
            </ExpandableSection>
          </div>
        )}
      </div>
    </Modal>
  );
}
